"use client";
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await signIn(email.trim(), password);
      router.replace('/home');
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const demoAccounts = [
    { label: 'Admin', email: 'admin@church.org', password: 'admin123' },
    { label: 'Pastor', email: 'pastor@church.org', password: 'pastor123' },
    { label: 'Business Office', email: 'business@church.org', password: 'biz123' },
    { label: 'Greeter', email: 'greeter@church.org', password: 'greet123' },
  ];

  return (
    <div className="min-h-screen bg-sanctuary flex items-center justify-center px-4">
      <div className="noise-overlay" aria-hidden="true" />
      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <AppLogo size={36} text="Compassion Check-in" className="text-silver" />
        </div>

        <div className="border border-border-strong bg-vestment p-8">
          <div className="mb-6">
            <span className="section-label block mb-2">Staff Portal</span>
            <h1 className="text-silver font-black text-2xl tracking-tight uppercase">Sign In</h1>
            <p className="text-silver-dim text-sm mt-1 font-light">Access your church management dashboard.</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="staff@church.org"
                required
                className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 placeholder:text-silver-dim/40 focus:outline-none focus:border-gold transition-colors"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 placeholder:text-silver-dim/40 focus:outline-none focus:border-gold transition-colors"
              />
            </div>

            {error && (
              <div className="border border-red-400/40 bg-red-400/10 px-4 py-3 font-mono text-xs text-red-400">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-gold w-full text-[11px] py-3 font-mono tracking-widest disabled:opacity-50"
            >
              {loading ? 'Signing In…' : 'Sign In'}
            </button>
          </form>

          {/* Demo accounts */}
          <div className="mt-6 pt-6 border-t border-border-subtle">
            <p className="font-mono text-[10px] tracking-widest uppercase text-silver-dim opacity-60 mb-3">Demo Accounts</p>
            <div className="grid grid-cols-2 gap-2">
              {demoAccounts.map(acc => (
                <button
                  key={acc.label}
                  type="button"
                  onClick={() => { setEmail(acc.email); setPassword(acc.password); }}
                  className="text-left border border-border-strong px-3 py-2 hover:border-gold transition-colors group"
                >
                  <span className="font-mono text-[10px] tracking-wider uppercase text-gold block">{acc.label}</span>
                  <span className="font-mono text-[9px] text-silver-dim opacity-60 group-hover:opacity-100 transition-opacity">{acc.email}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Role descriptions */}
        <div className="mt-4 border border-border-subtle p-4">
          <p className="font-mono text-[9px] tracking-widest uppercase text-silver-dim opacity-40 mb-2">Role Access Levels</p>
          <div className="space-y-1">
            {[
              { roles: 'Admin, Church Staff', access: 'Full access to all features' },
              { roles: 'Pastor, Business Office', access: 'View reports + receive email reports' },
              { roles: 'Greeter, Usher', access: 'Attendance recording only' },
            ].map(r => (
              <div key={r.roles} className="flex gap-3">
                <span className="font-mono text-[9px] text-gold opacity-70 w-36 flex-shrink-0">{r.roles}</span>
                <span className="font-mono text-[9px] text-silver-dim opacity-50">{r.access}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
